import React, { useEffect } from 'react';
import { IntercomProvider } from 'react-use-intercom';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getIntercomConfig,
  getIntercomLoadingState,
} from 'app/store/Intercom/selectors';
import { fetchAuthLinksEffect } from 'app/store/Auth/effects.utils';
import {
  getApiKeyWebLoadingState,
  getAuthenticatedSelector,
  getBootstrapLoadingState,
} from 'app/store/Auth/selectors';
import { isLoading } from 'app/utils/utils';
import {
  useAppRedirect,
  useDeeplinkAction,
  useDeeplinking,
  useElectronForceUpdate,
  useLinkMiddleMouseHandler,
} from 'app/utils/hooks';
import { config } from 'config';
import { initializeAppsFlyer } from 'app/utils/appsFlyer';
import { fetchFoldersEffect } from 'app/store/SongList/effects';
import { ChakraAppLoader } from '../Core';
import { initializeGoogleTagManager } from './utils';
import {
  useFetchStorageData,
  useFetchSubscriptionsData,
  useIntercomData,
  useSetMinimumAppVersion,
} from './hooks';
import { OfflineTracker } from '../Offline/OfflineTracker';

interface InitializationLayerProps {
  children?: React.ReactNode;
}

export const InitializationLayer: React.FC<InitializationLayerProps> = ({
  children,
}) => {
  useAppRedirect();
  useDeeplinking();
  useDeeplinkAction();
  useLinkMiddleMouseHandler();
  useElectronForceUpdate();
  useFetchStorageData();
  useFetchSubscriptionsData();
  useIntercomData();

  const dispatch = useAppDispatch();
  const intercomConfig = useAppSelector(getIntercomConfig);
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const apiKeyWebLoadingState = useAppSelector(getApiKeyWebLoadingState);
  const bootstrapLoadingState = useAppSelector(getBootstrapLoadingState);
  const intercomLoadingState = useAppSelector(getIntercomLoadingState);
  const bootstrapLoading = isLoading(bootstrapLoadingState);
  const apiKeyLoading = isLoading(apiKeyWebLoadingState);
  const intercomLoading = isLoading(intercomLoadingState);

  useSetMinimumAppVersion(bootstrapLoading);

  const isInitializing = bootstrapLoading || intercomLoading || apiKeyLoading;

  useEffect(() => {
    dispatch(fetchAuthLinksEffect());
    if (isAuthenticated) {
      dispatch(fetchFoldersEffect());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    initializeAppsFlyer();
    if (!config.isElectronBuild || !config.isFeatureBuild) {
      initializeGoogleTagManager(); // This part initializes GTM connected with www.sessionstudio.com
    }
  }, []);

  if (isInitializing) {
    return (
      <>
        <OfflineTracker />
        <ChakraAppLoader />
      </>
    );
  }
  return intercomConfig?.appId ? (
    <IntercomProvider appId={intercomConfig.appId}>{children}</IntercomProvider>
  ) : (
    <>{children}</>
  );
};
